/**
 * How wide the strings a blank can hold really are, in the face that prints them.
 *
 * A fill-in-the-blank field is a fixed-width box in the middle of a sentence, so
 * its width has to be decided before it is laid out — and deciding it from a
 * character count times an average glyph width is a guess, not a measurement. The
 * guess is wrong exactly where this app cares: a themed book prints in the
 * theme's own face, and the bundled hands are a third wider than the average it
 * assumes, so a field sized for Inter cuts the answer off in a book written in a
 * hand.
 *
 * So the platform is asked. Each candidate string — the answer, its accepted
 * alternatives, the words on offer, whatever has been typed — is rendered at zero
 * opacity, out of the page's flow but *still laid out*, and reports its own width
 * through `onLayout`. That is the same text engine, at the same size, in the same
 * face, with the same ligatures and joining as the field it is sizing: the only
 * way to be right in every theme's font without shipping a font metrics library.
 *
 * Two things make it safe:
 *
 *  - **It is not a layout element.** The probe is absolutely positioned with
 *    `pointerEvents="none"` and `aria-hidden`, so it takes no space, catches no
 *    taps and is not announced. `opacity: 0` rather than `display: none` on
 *    purpose — a hidden element is not laid out, and an unlaid-out element has no
 *    width to report.
 *  - **It cannot grow the page it measures.** Its own width is capped at
 *    `maxWidth: '100%'` of its container, and the field it sizes is capped by
 *    `blankInputWidth`, so a probe can never push the sentence it belongs to.
 *
 * The widths are cached for the life of the process, keyed by the font as well as
 * the string, so the second blank asking for the same word is right on its first
 * render. A probe re-reports whenever its box changes, which is what makes the
 * cache correct after a bundled hand finishes loading: the first measurement is
 * taken in the fallback face and overwritten by the real one.
 */
import {type ReactNode, useCallback, useState} from 'react'
import {
  type LayoutChangeEvent,
  StyleSheet,
  Text,
  type TextStyle,
  View,
} from 'react-native'

import {BLANK_MAX_WIDTH} from '#/lib/blankInputWidth'

/** Widths measured on this run, by font and by string. */
const measured = new Map<string, number>()

/** Everything two measurements must agree on to be the same measurement. */
function fontKey(font: TextStyle): string {
  return [
    font.fontFamily ?? '',
    font.fontSize ?? '',
    font.fontWeight ?? '',
    font.fontStyle ?? '',
    font.letterSpacing ?? '',
  ].join('\u0000')
}

function cacheKey(font: TextStyle, text: string): string {
  return `${fontKey(font)}\u0000${text}`
}

export type TextWidths = {
  /** The measured width of each candidate the platform has reported. */
  widths: Map<string, number>
  /**
   * The probe to render once inside the page being measured.
   *
   * Rendered at the page's own level rather than inside the sentence: a probe
   * inside the wrapping row would be measured against a width that the blanks
   * themselves decide, which is a circle.
   */
  probe: ReactNode
}

export function useTextWidths(
  font: TextStyle,
  candidates: readonly (string | undefined | null)[],
): TextWidths {
  const [, bump] = useState(0)

  /*
   * Read straight from the cache on every render rather than holding it in state:
   * the cache is the measurement, and state here would only be a second copy of
   * it that could disagree. The `bump` is the one thing state is for — a
   * measurement landing is what schedules the render that uses it.
   */
  const seen = new Set<string>()
  const unmeasured: string[] = []
  const widths = new Map<string, number>()
  for (const candidate of candidates) {
    const text = (candidate ?? '').trim()
    if (!text || seen.has(text)) continue
    seen.add(text)
    const width = measured.get(cacheKey(font, text))
    if (width !== undefined) widths.set(text, width)
    else unmeasured.push(text)
  }

  const report = useCallback(
    (text: string, width: number) => {
      const key = cacheKey(font, text)
      if (!Number.isFinite(width) || width <= 0) return
      if (measured.get(key) === width) return
      measured.set(key, width)
      bump(n => n + 1)
    },
    [font],
  )

  const probe = unmeasured.length ? (
    <View aria-hidden pointerEvents="none" style={styles.probe}>
      {unmeasured.map(text => (
        <Text
          key={text}
          // No `numberOfLines`: a candidate longer than the room available wraps
          // and reports the room, which is the cap the field would hit anyway.
          // Clamping it here would report a width the field could never use.
          style={[font, styles.probeText]}
          onLayout={(event: LayoutChangeEvent) =>
            report(text, event.nativeEvent.layout.width)
          }>
          {text}
        </Text>
      ))}
    </View>
  ) : null

  return {widths, probe}
}

const styles = StyleSheet.create({
  probe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: BLANK_MAX_WIDTH,
    // Never wider than the page it is measuring on, so the probe cannot itself
    // become the overflow a layout test finds.
    maxWidth: '100%',
    alignItems: 'flex-start',
    opacity: 0,
  },
  probeText: {opacity: 0},
})
